import type { GenerateRequest, GenerateResult, GenerateErrorCode } from './types';
import {
  generateUrl,
  feedbackUrl,
  EMPLOYER_DIDE_WRITER_ENDPOINT,
  type DideWriterEndpointBase,
} from './endpoints';

const readCsrf = (): string => {
  if (typeof document === 'undefined') return '';
  const el = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
  return el?.content ?? '';
};

// Maps HTTP status + payload to a stable error code the UI can switch on.
// The server-provided `code` always wins; fall back to status-only mapping
// when the payload does not include one (e.g. upstream proxy error pages).
const normalizeErrorCode = (status: number, payloadCode?: string): GenerateErrorCode => {
  if (payloadCode) return payloadCode as GenerateErrorCode;
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden_practice';
  if (status === 404) return 'not_found';
  if (status === 422) return 'invalid_input';
  if (status === 429) return 'rate_limited';
  if (status === 500) return 'internal_error';
  if (status === 502) return 'ai_unavailable';
  return 'unknown';
};

export interface ApiOptions {
  signal?: AbortSignal;
  endpointBase?: DideWriterEndpointBase;
  timeoutMs?: number;
}

// The server answers within about 45s (it returns ai_unavailable when the
// providers take longer). The client gives up a little after that.
export const GENERATE_TIMEOUT_MS = 60_000;

export async function generateJobDescription(
  payload: GenerateRequest,
  opts: ApiOptions = {}
): Promise<GenerateResult> {
  const url = generateUrl(opts.endpointBase ?? EMPLOYER_DIDE_WRITER_ENDPOINT);
  const controller = new AbortController();
  const forwardAbort = () => controller.abort();
  opts.signal?.addEventListener('abort', forwardAbort);
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, opts.timeoutMs ?? GENERATE_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': readCsrf(),
      },
      body: JSON.stringify(payload),
    });
  } catch (networkErr) {
    if ((networkErr as { name?: string }).name === 'AbortError') {
      if (timedOut) {
        return {
          success: false,
          error: 'We took too long to respond. Please try again.',
          code: 'ai_unavailable',
        };
      }
      throw networkErr;
    }
    return {
      success: false,
      error: 'Network error. Please check your connection and try again.',
      code: 'unknown',
    };
  } finally {
    clearTimeout(timer);
    opts.signal?.removeEventListener('abort', forwardAbort);
  }

  const body = await response.json().catch(() => ({}));

  if (response.ok && body.success) {
    return body as GenerateResult;
  }

  return {
    success: false,
    error: (body && (body.error || body.message)) || `Request failed (${response.status})`,
    code: normalizeErrorCode(response.status, body?.code),
    limit: body?.limit,
    scope: body?.scope,
    details: body?.details,
  };
}

export interface FeedbackPayload {
  rating?: number; // 1..5
  comment?: string;
}

export interface FeedbackResult {
  success: boolean;
  error?: string;
  code?: GenerateErrorCode;
}

export async function submitJobDescriptionFeedback(
  draftId: number,
  feedback: FeedbackPayload,
  opts: ApiOptions = {}
): Promise<FeedbackResult> {
  const url = feedbackUrl(draftId, opts.endpointBase ?? EMPLOYER_DIDE_WRITER_ENDPOINT);
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      signal: opts.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': readCsrf(),
      },
      body: JSON.stringify({
        feedback_rating: feedback.rating,
        feedback_comment: feedback.comment,
      }),
    });
  } catch (networkErr) {
    if ((networkErr as { name?: string }).name === 'AbortError') throw networkErr;
    return {
      success: false,
      error: 'Network error. Please try again.',
      code: 'unknown',
    };
  }

  const body = await response.json().catch(() => ({}));

  if (response.ok && body.success) {
    return { success: true };
  }

  return {
    success: false,
    error: (body && (body.error || body.message)) || `Request failed (${response.status})`,
    code: normalizeErrorCode(response.status, body?.code),
  };
}
